/* global AccountKit */
import FbUtil from 'app/common/lib/FbUtil';
import runValidators from 'app/common/react/components/growth/unauth/lib/ValidationUtils';
import templateConst from 'app/common/lib/templateConst';
import { PhoneNumber, Required } from 'app/common/lib/react/validator';

const phoneNumberAsYouTypeParser = () =>
  import(/* webpackChunkName: "PhoneNumberAsYouTypeParserCommon" */ 'app/common/react/components/growth/unauth/signup/PhoneAuth/PhoneNumberAsYouTypeParser');

const PhoneAuth = {
  PhoneNumberAsYouTypeParser: null,

  csrf: Math.floor(Math.random() * 0xffffffffffff).toString(16),

  phoneNumberValidators: [Required(), PhoneNumber()],

  /**
   * Sets up PhoneAuth, i.e. Facebook Account Kit and libphonenumber-js.
   * This should be called in componentDidMount().
   * @param {String} locale
   */
  setupPhoneAuth: (locale) => {
    // prepare async function to init AccountKit
    const csrf = PhoneAuth.csrf;
    window.AccountKit_OnInteractive = function() {
      AccountKit.init({
        appId: templateConst.settings.FACEBOOK_API_KEY,
        state: csrf,
        version: 'v1.1',
        fbAppEventsEnabled: true,
      });
    };

    // async load AccountKit sdk.js
    ((d, s, id) => {
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      const js = d.createElement(s);
      js.id = id;
      js.src = 'https://sdk.accountkit.com/' + FbUtil.getLocaleString(locale) + '/sdk.js';
      fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'accountkit-jssdk');

    // load libphonenumber-js async
    phoneNumberAsYouTypeParser().then((chunk) => {
      PhoneAuth.PhoneNumberAsYouTypeParser = chunk.default;
    });
  },

  /**
   * Runs the PhoneNumber validators, and if they pass runs Account Kit.
   * @param {String} phoneNumberStr
   * @param {String} locale
   * @param {Func} callback for Facebook Account Kit
   * @return {Object} error from validators
   */
  runPhoneValidators: (phoneNumberStr, locale, callback) => {
    const error = runValidators(phoneNumberStr, PhoneAuth.phoneNumberValidators);
    if (!error) {
      // try/catch to avoid any possible loading issue
      try {
        // parse phone number
        const defaultCountry = PhoneAuth._extractCountryCode(locale);
        const phoneFormatter = new PhoneAuth.PhoneNumberAsYouTypeParser(defaultCountry);
        phoneFormatter.input(phoneNumberStr);
        // open Account Kit popup
        AccountKit.login(
          'PHONE',
          {
            countryCode: PhoneAuth._extractPhoneCountryCode(phoneFormatter),
            phoneNumber: PhoneAuth._extractPhoneNumber(phoneFormatter),
          },
          callback,
        );
      } catch (err) {
        return true;
      }
    }
    return error;
  },

  /**
   * Validates a response from Facebook Account Kit.
   * @param {Object} response
   * @return {Boolean} valid response?
   */
  validateResponse: (response) =>
    response && response.status === 'PARTIALLY_AUTHENTICATED' && response.state === PhoneAuth.csrf,

  /**
   * Returns country code, e.g. US, IT.
   * This is used as a default, nothing breaks if the value isn't correct.
   * @param {String} locale
   * @return {String} country code
   */
  _extractCountryCode: (locale) => locale.slice(-2).toUpperCase() || 'US',

  /**
   * Returns country phone code, e.g. +1, +39.
   * @param {Object} phoneFormatter
   * @return {String} country phone code
   */
  _extractPhoneCountryCode: (phoneFormatter) => {
    if (phoneFormatter.country_phone_code) {
      return '+' + phoneFormatter.country_phone_code;
    }
    if (phoneFormatter.country && phoneFormatter.country_metadata.length > 0) {
      return '+' + phoneFormatter.country_metadata[0];
    }
    return '+1';
  },

  /**
   * Returns phone number, without country phone code
   * @param {Object} phoneFormatter
   * @return {String} phone number
   */
  _extractPhoneNumber: (phoneFormatter) => phoneFormatter.national_number,
};

export default PhoneAuth;
